import React, { useEffect, useMemo, useState } from "react";

import api from "@/lib/axios";

const TABS = [
  { key: "planners", label: "PO planners" },
  { key: "cost_centres", label: "Cost centres" },
  { key: "events", label: "Events (optional)" },
];

function number(value) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
}

function money(value) {
  return new Intl.NumberFormat("en-GB", {
    style: "currency",
    currency: "GBP",
    maximumFractionDigits: 0,
  }).format(number(value));
}

function rowsFrom(body) {
  return [
    body?.purchase_orders,
    body?.rows,
    body?.data,
    body?.results,
  ].find(Array.isArray) || [];
}

function groupName(tab, row) {
  if (tab === "planners") {
    return row.planner_name || "Unassigned PO planner";
  }

  if (tab === "cost_centres") {
    return row.cost_centre_name || "Unassigned PO cost centre";
  }

  return row.event_name || "Unnamed event";
}

function groupFilter(tab, row) {
  if (tab === "planners" && row.planner_id != null) {
    return { planner_id: row.planner_id };
  }

  if (tab === "cost_centres" && row.cost_centre_id != null) {
    return { cost_centre_id: row.cost_centre_id };
  }

  if (tab === "events" && row.event_id != null) {
    return { event_id: row.event_id };
  }

  return null;
}

function missingPredicate(tab) {
  if (tab === "planners") {
    return (row) => row.po_planner_missing === true;
  }

  if (tab === "cost_centres") {
    return (row) => row.po_cost_centre_missing === true;
  }

  return null;
}

export default function ProcurementDecisionBreakdowns({ params = {} }) {
  const [activeTab, setActiveTab] = useState("planners");
  const [summary, setSummary] = useState(null);
  const [breakdowns, setBreakdowns] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [showAll, setShowAll] = useState(false);
  const [drawer, setDrawer] = useState(null);
  const [drawerLoading, setDrawerLoading] = useState(false);
  const [drawerError, setDrawerError] = useState("");

  const requestKey = JSON.stringify(params);
  const requestParams = useMemo(
    () => JSON.parse(requestKey),
    [requestKey]
  );

  useEffect(() => {
    let cancelled = false;

    async function load() {
      setLoading(true);
      setError("");
      setDrawer(null);

      try {
        const [summaryResult, breakdownResult] = await Promise.all([
          api.get("/reports/procurement-decision/summary", {
            params: requestParams,
          }),
          api.get("/reports/procurement-decision/breakdowns", {
            params: requestParams,
          }),
        ]);

        if (!cancelled) {
          setSummary(summaryResult.data?.summary || {});
          setBreakdowns(breakdownResult.data?.breakdowns || {});
        }
      } catch (loadError) {
        if (!cancelled) {
          setError(
            loadError?.response?.data?.message ||
              "Unable to load procurement decision intelligence."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    load();
    return () => {
      cancelled = true;
    };
  }, [requestKey, refreshKey]);

  const groups = useMemo(() => {
    const source = Array.isArray(breakdowns?.[activeTab])
      ? breakdowns[activeTab]
      : [];

    return [...source].sort(
      (a, b) =>
        number(b.operational_attention) -
          number(a.operational_attention) ||
        number(b.overdue) - number(a.overdue) ||
        number(b.purchase_orders) - number(a.purchase_orders)
    );
  }, [activeTab, breakdowns]);

  async function fetchAll(predicate) {
    const collected = [];
    let page = 1;
    let total = Infinity;

    while (collected.length < total && page <= 20) {
      const response = await api.get(
        "/reports/procurement-decision/purchase-orders",
        {
          params: {
            ...requestParams,
            page,
            page_size: 200,
          },
        }
      );

      const pageRows = rowsFrom(response.data);
      collected.push(...pageRows);
      total = number(
        response.data?.pagination?.total ??
          response.data?.total ??
          collected.length
      );

      if (!pageRows.length) break;
      page += 1;
    }

    return predicate ? collected.filter(predicate) : collected;
  }

  async function openGroup(row) {
    const label = groupName(activeTab, row);
    const filter = groupFilter(activeTab, row);
    const predicate = filter ? null : missingPredicate(activeTab);

    setDrawer({ title: label, rows: [], total: 0 });
    setDrawerLoading(true);
    setDrawerError("");

    try {
      let purchaseOrders;
      let total;

      if (filter) {
        const response = await api.get(
          "/reports/procurement-decision/purchase-orders",
          {
            params: {
              ...requestParams,
              ...filter,
              page: 1,
              page_size: 50,
            },
          }
        );

        purchaseOrders = rowsFrom(response.data);
        total = number(
          response.data?.pagination?.total ??
            response.data?.total ??
            purchaseOrders.length
        );
      } else {
        purchaseOrders = await fetchAll(predicate);
        total = purchaseOrders.length;
      }

      setDrawer({ title: label, rows: purchaseOrders, total });
    } catch (loadError) {
      setDrawerError(
        loadError?.response?.data?.message ||
          "Unable to load the purchase orders for this group."
      );
    } finally {
      setDrawerLoading(false);
    }
  }

  async function openMandatoryReferences() {
    setDrawer({
      title: "Missing mandatory PO references",
      rows: [],
      total: 0,
    });
    setDrawerLoading(true);
    setDrawerError("");

    try {
      const purchaseOrders = await fetchAll(
        (row) =>
          row.po_planner_missing === true ||
          row.po_cost_centre_missing === true
      );

      setDrawer({
        title: "Missing mandatory PO references",
        rows: purchaseOrders,
        total: purchaseOrders.length,
      });
    } catch (loadError) {
      setDrawerError(
        loadError?.response?.data?.message ||
          "Unable to load the affected purchase orders."
      );
    } finally {
      setDrawerLoading(false);
    }
  }

  if (loading) {
    return (
      <section className="rounded-2xl border border-white/10 bg-white/[0.04] p-5">
        <div className="h-5 w-56 animate-pulse rounded bg-white/10" />
        <div className="mt-4 grid gap-3 md:grid-cols-4">
          {[0, 1, 2, 3].map((item) => (
            <div
              key={item}
              className="h-24 animate-pulse rounded-xl bg-white/[0.06]"
            />
          ))}
        </div>
      </section>
    );
  }

  if (error) {
    return (
      <section className="rounded-2xl border border-rose-400/30 bg-rose-500/10 p-5 text-white">
        <p className="font-semibold">Decision intelligence unavailable</p>
        <p className="mt-1 text-sm text-rose-100/80">{error}</p>
        <button
          type="button"
          onClick={() => setRefreshKey((value) => value + 1)}
          className="mt-4 rounded-lg border border-rose-200/30 px-3 py-2 text-sm font-semibold hover:bg-white/10"
        >
          Try again
        </button>
      </section>
    );
  }

  const missingPlanner = number(summary?.po_planner_missing);
  const missingCostCentre = number(summary?.po_cost_centre_missing);
  const hasMandatoryWarning = missingPlanner + missingCostCentre > 0;
  const visibleGroups = showAll ? groups : groups.slice(0, 10);

  return (
    <>
      <section className="space-y-4 rounded-2xl border border-cyan-300/20 bg-gradient-to-br from-cyan-400/[0.08] via-white/[0.03] to-indigo-400/[0.08] p-5 text-white shadow-2xl shadow-cyan-950/20">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
          <div>
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-cyan-300">
              Procurement decision intelligence
            </p>
            <h2 className="mt-1 text-xl font-bold">Ownership and exposure</h2>
            <p className="mt-1 text-sm text-zinc-300">
              Uses the same report period and filters shown above. Event links remain optional.
            </p>
          </div>

          <button
            type="button"
            onClick={() => setRefreshKey((value) => value + 1)}
            className="self-start rounded-lg border border-white/15 bg-white/[0.05] px-3 py-2 text-sm font-semibold hover:bg-white/10"
          >
            Refresh intelligence
          </button>
        </div>

        {hasMandatoryWarning && (
          <button
            type="button"
            onClick={openMandatoryReferences}
            className="flex w-full flex-col gap-2 rounded-xl border border-amber-300/30 bg-amber-400/10 p-4 text-left transition hover:bg-amber-400/15 sm:flex-row sm:items-center sm:justify-between"
          >
            <span>
              <span className="block font-semibold text-amber-100">
                Historical POs need mandatory-reference cleanup
              </span>
              <span className="mt-1 block text-sm text-amber-100/75">
                {missingPlanner} missing PO planner assignment{missingPlanner === 1 ? "" : "s"} · {missingCostCentre} missing PO cost-centre assignment{missingCostCentre === 1 ? "" : "s"}
              </span>
            </span>
            <span className="text-sm font-bold text-amber-200">Review POs →</span>
          </button>
        )}

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          {[
            ["Purchase orders", summary?.purchase_orders],
            ["Need attention", summary?.operational_attention],
            ["Overdue", summary?.overdue],
            ["Remaining exposure", money(summary?.actionable_remaining_net_value)],
          ].map(([label, value]) => (
            <div key={label} className="rounded-xl border border-white/10 bg-black/20 p-4">
              <p className="text-xs uppercase tracking-wide text-zinc-400">{label}</p>
              <p className="mt-1 text-2xl font-bold">{value ?? 0}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-2 border-b border-white/10 pb-3">
          {TABS.map((tab) => (
            <button
              key={tab.key}
              type="button"
              onClick={() => {
                setActiveTab(tab.key);
                setShowAll(false);
              }}
              className={`rounded-full px-4 py-2 text-sm font-semibold transition ${
                activeTab === tab.key
                  ? "bg-cyan-300 text-slate-950"
                  : "bg-white/[0.06] text-zinc-300 hover:bg-white/10"
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {groups.length === 0 ? (
          <div className="rounded-xl border border-dashed border-white/15 p-8 text-center text-sm text-zinc-400">
            No {TABS.find((tab) => tab.key === activeTab)?.label.toLowerCase()} match the current filters.
          </div>
        ) : (
          <div className="overflow-x-auto rounded-xl border border-white/10">
            <table className="min-w-full divide-y divide-white/10 text-sm">
              <thead className="bg-black/25 text-left text-xs uppercase tracking-wide text-zinc-400">
                <tr>
                  <th className="px-4 py-3">Owner / allocation</th>
                  <th className="px-4 py-3 text-right">POs</th>
                  <th className="px-4 py-3 text-right">Attention</th>
                  <th className="px-4 py-3 text-right">Overdue</th>
                  <th className="px-4 py-3 text-right">Data quality</th>
                  <th className="px-4 py-3 text-right">Remaining</th>
                  <th className="px-4 py-3 text-right">Complete</th>
                  <th className="px-4 py-3" />
                </tr>
              </thead>
              <tbody className="divide-y divide-white/[0.07]">
                {visibleGroups.map((row, index) => (
                  <tr key={`${activeTab}-${row.planner_id ?? row.cost_centre_id ?? row.event_id ?? index}`} className="bg-white/[0.02] hover:bg-white/[0.05]">
                    <td className="px-4 py-3">
                      <p className="font-semibold text-white">{groupName(activeTab, row)}</p>
                      {row.is_unassigned && (
                        <p className="mt-0.5 text-xs text-amber-300">Mandatory PO reference missing</p>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right tabular-nums">{number(row.purchase_orders)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-amber-200">{number(row.operational_attention)}</td>
                    <td className="px-4 py-3 text-right tabular-nums text-rose-200">{number(row.overdue)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{number(row.data_quality_warnings)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{money(row.actionable_remaining_net_value)}</td>
                    <td className="px-4 py-3 text-right tabular-nums">{number(row.receiving_completion_percent).toFixed(1)}%</td>
                    <td className="px-4 py-3 text-right">
                      <button
                        type="button"
                        onClick={() => openGroup(row)}
                        className="whitespace-nowrap font-semibold text-cyan-300 hover:text-cyan-200"
                      >
                        View POs →
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {groups.length > 10 && (
          <button
            type="button"
            onClick={() => setShowAll((value) => !value)}
            className="text-sm font-semibold text-cyan-300 hover:text-cyan-200"
          >
            {showAll ? "Show top 10" : `Show all ${groups.length} groups`}
          </button>
        )}
      </section>

      {drawer && (
        <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 p-3 backdrop-blur-sm sm:items-center">
          <div className="max-h-[85vh] w-full max-w-5xl overflow-hidden rounded-2xl border border-white/15 bg-slate-950 text-white shadow-2xl">
            <div className="flex items-start justify-between border-b border-white/10 p-5">
              <div>
                <p className="text-xs font-semibold uppercase tracking-[0.2em] text-cyan-300">Purchase-order drill-down</p>
                <h3 className="mt-1 text-lg font-bold">{drawer.title}</h3>
                {!drawerLoading && !drawerError && (
                  <p className="mt-1 text-sm text-zinc-400">{drawer.total} matching purchase order{drawer.total === 1 ? "" : "s"}</p>
                )}
              </div>
              <button
                type="button"
                aria-label="Close drill-down"
                onClick={() => setDrawer(null)}
                className="rounded-lg border border-white/10 px-3 py-2 text-zinc-300 hover:bg-white/10"
              >
                Close
              </button>
            </div>

            <div className="max-h-[65vh] overflow-auto p-5">
              {drawerLoading ? (
                <p className="text-sm text-zinc-400">Loading purchase orders…</p>
              ) : drawerError ? (
                <p className="rounded-lg bg-rose-500/10 p-4 text-sm text-rose-200">{drawerError}</p>
              ) : drawer.rows.length === 0 ? (
                <p className="text-sm text-zinc-400">No purchase orders match this selection.</p>
              ) : (
                <div className="space-y-2">
                  {drawer.rows.map((row) => (
                    <div key={row.po_id} className="grid gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 md:grid-cols-[1.2fr_1.4fr_1fr_1fr_auto] md:items-center">
                      <div>
                        <p className="font-semibold">{row.po_number || `PO #${row.po_id}`}</p>
                        <p className="text-xs text-zinc-400">{row.po_status}</p>
                      </div>
                      <p className="text-sm text-zinc-300">{row.vendor_name || "Vendor unavailable"}</p>
                      <p className="text-sm">{row.action_owner || "Unassigned PO planner"}</p>
                      <p className="text-sm font-semibold">{money(row.actionable_remaining_net_value)}</p>
                      <div className="flex flex-wrap gap-1 md:justify-end">
                        {row.po_planner_missing && <span className="rounded bg-amber-400/15 px-2 py-1 text-xs text-amber-200">Planner missing</span>}
                        {row.po_cost_centre_missing && <span className="rounded bg-amber-400/15 px-2 py-1 text-xs text-amber-200">Cost centre missing</span>}
                        {!row.po_planner_missing && !row.po_cost_centre_missing && row.decision_priority && (
                          <span className="rounded bg-white/10 px-2 py-1 text-xs">{row.decision_priority}</span>
                        )}
                      </div>
                    </div>
                  ))}
                  {drawer.total > drawer.rows.length && (
                    <p className="pt-2 text-center text-xs text-zinc-500">Showing the first {drawer.rows.length} of {drawer.total} matching POs.</p>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
